import { useCallback } from 'react';
import { useGetTransportQuery, useGetDashboardOptionsQuery, type DashboardOptions } from '@/entities/dashboard';
import { useUrlState, useProjectSiteParams } from '@/shared/lib';
import type { DateRangeValue } from '@/shared/ui';

import { TransportUrlState } from '../model/types';

const DEFAULT_URL_STATE: TransportUrlState = {
  metric: 'W',
  group: 'D',
  period: 'L6M',
  status: 'DI',
  breakDown: 'NO',
  vehicleCategoryId: 'NO',
  fromDate: '',
  toDate: ''
};

export function useTransportOverview() {
  const { projectId, siteId } = useProjectSiteParams();
  const [urlState, setUrlState, resetUrlState] = useUrlState(DEFAULT_URL_STATE);

  const { data, isLoading, isFetching, error } = useGetTransportQuery({
    ...urlState,
    projectId,
    siteId
  });

  const { data: optionsData } = useGetDashboardOptionsQuery();

  const updateOption = useCallback(
    (key: keyof DashboardOptions, value: string) => {
      setUrlState({ [key]: value });
    },
    [setUrlState]
  );

  const handleDateRangeChange = useCallback(
    (dateRange: DateRangeValue) => {
      setUrlState(dateRange as TransportUrlState);
    },
    [setUrlState]
  );

  const formatValue = useCallback((value: number, metric?: string): string => {
    if (metric === 'W') {
      return `${(value / 1000).toFixed(1)} mt`;
    }
    if (metric === 'V') {
      return `${value.toFixed(1)} m³`;
    }
    return value.toLocaleString();
  }, []);

  const formattedStats = data
    ? {
        totalShipments: data.totalNumberOfShipments.toLocaleString(),
        totalTransported: formatValue(data.totalTransported, urlState.metric),
        totalDestinations: data.totalNumberOfDestinations.toString(),
        totalLoss: formatValue(data.totalLoss, urlState.metric),
        totalDamage: formatValue(data.totalDamage, urlState.metric)
      }
    : null;

  return {
    // Data
    data,
    formattedStats,
    vehicleCategories: optionsData?.vehicleCategories,

    // Loading states
    isLoading,
    isFetching,
    error,

    // Filters
    urlState,

    // Handlers
    updateOption,
    handleDateRangeChange,
    resetFilters: resetUrlState,

    // Project/Site
    projectId,
    siteId
  };
}
